/**
 * Reference Guides — content model.
 *
 * Two levels, deliberately:
 *
 *   ReferenceGuide
 *     └── Section[]     owns { id, heading, tocLabel }. IS the TOC. IS the URL anchor.
 *           └── Block[] presentational. No id. Never appears in the TOC.
 *
 * Section.id is a PUBLIC URL CONTRACT (.../procurement-systems#pr-vs-po).
 * Renaming one breaks inbound links and search snippets. Blocks can be
 * reordered, added, and removed inside a section without touching any URL.
 *
 * EXTENSION POLICY — read before adding guide #4.
 * "A new guide costs a data file" is only true when an existing block expresses
 * the content faithfully. Quality/QMS, CPQ, and scheduling will bring swimlanes,
 * formulas, and decision trees that none of these blocks model. When that
 * happens the answer is a NEW BLOCK KIND plus a renderer case — never a `prose`
 * block stuffed with foreign structure. The renderer's assertNever will force
 * the issue at compile time.
 */

/** 2 = primary owner, 1 = secondary/contributes, 0 = not applicable. */
export type Ownership = 0 | 1 | 2;
export type CalloutVariant = 'warning' // amber  — "the core gap"
| 'flag' // rose   — "red flag" / "right fit signal"
| 'tip' // emerald — "how to read this"
| 'info' // violet  — neutral aside
| 'bottomLine'; // emerald — "the bottom line"

export type CategoryAccent = 'inventory' | 'manufacturing' | 'procurement';
export interface PillarItem {
  /** Short badge text: "PR", "RFQ", "ERP", "WMS". */
  tag: string;
  title: string;
  /** "The need layer", "The sourcing layer". */
  layer: string;
  body: string;
  /** Highlights the commitment/close steps in a chain. */
  emphasis?: 'accent' | 'warn';
}
export interface LifecycleStep {
  n: number;
  title: string;
  body: string;
}
export interface LifecycleStage {
  /** "Stage 1", "Phase 2 — Sourcing". */
  label: string;
  title: string;
  steps: LifecycleStep[];
  /** The "who drives this phase" footnote. Leading clause is bolded. */
  note: string;
  noteEmphasis?: 'accent' | 'success';
}
export interface CompareCard {
  tag: string;
  title: string;
  body: string;
  bullets: string[];
  emphasis?: 'accent' | 'warn';
}
export interface MatrixRow {
  capability: string;
  /** Must be exactly columns.length long — enforced by validateGuides. */
  values: Ownership[];
}
export interface FlowNode {
  label: string;
  title: string;
  body: string;
  /** Thruline-owned nodes render with the accent treatment. */
  owned?: boolean;
}
export interface ScenarioFeature {
  title: string;
  body: string;
}
export interface Scenario {
  n: number;
  /** "If you already have an ERP with purchasing in place". */
  condition: string;
  headline: string;
  body: string;
  flow?: FlowNode[];
  /** Connector glyphs between flow nodes; length must be flow.length - 1. */
  flowConnectors?: Array<'sync' | 'next'>;
  footnote?: string;
  features?: ScenarioFeature[];
}
export interface SolutionLink {
  label: string;
  /** Must match a route registered in App.tsx — enforced by validateGuides. */
  to: string;
}
export type Block = {
  kind: 'pillars';
  columns: 3 | 4 | 5;
  items: PillarItem[];
} | {
  kind: 'lifecycle';
  stages: LifecycleStage[];
} | {
  kind: 'compare';
  cards?: CompareCard[];
  columns: string[];
  rows: string[][];
} | {
  kind: 'matrix';
  columns: string[];
  rows: MatrixRow[];
  legendNote?: string;
} | {
  kind: 'callout';
  variant: CalloutVariant;
  title: string;
  body: string;
} | {
  kind: 'prose';
  paragraphs: string[];
} | {
  kind: 'thrulineFit';
  intro: string;
  scenarios: Scenario[];
  solutions: SolutionLink[];
};
export interface Section {
  /** PUBLIC URL CONTRACT — see file header. Slug-safe, unique within a guide. */
  id: string;
  heading: string;
  /** Short label for the sticky rail. */
  tocLabel: string;
  /** Optional standfirst under the heading. */
  standfirst?: string;
  blocks: Block[];
}
export interface GuideCta {
  headline: string;
  button: string;
}
export interface ReferenceGuide {
  slug: string;
  category: 'Inventory' | 'Manufacturing' | 'Procurement';
  accent: CategoryAccent;
  title: string;
  subtitle: string;
  /** The diagnostic question shown on the index card. Does the selling. */
  question: string;
  /** Meta description. Kept separate from subtitle so it can be tuned for SERPs. */
  description: string;
  readMinutes: number;
  /** Path under public/. Existence enforced by validateGuides. */
  pdf: string;
  cta?: GuideCta;
  sections: Section[];
}

/** A guide that exists as a card but has no page yet. */
export interface PlannedGuide {
  status: 'planned';
  title: string;
  category: string;
}

/** Exhaustiveness guard. Adding a Block kind without a renderer case fails the build. */
export function assertNever(value: never, context: string): never {
  throw new Error(`${context}: unhandled variant ${JSON.stringify(value)}. ` + `Add a case for it — see the extension policy in data/referenceGuides/types.ts.`);
}